import { useAppDispatch, useAppSelector } from 'hooks/redux';
// CORE
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
import type { List as ImList, Set } from 'immutable';
import { Map as ImmutableMap } from 'immutable';
import React, { useState, useMemo, useCallback } from 'react';
import { useToggle } from 'react-use';

import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';

// MUI ICONS
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExploreIcon from '@mui/icons-material/Explore';
import ExploreOffIcon from '@mui/icons-material/ExploreOff';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotInterestedRoundedIcon from '@mui/icons-material/NotInterestedRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';

import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { accountDiscoveryActions, aggErrors, aggErrorsSelectors } from 'app-components/flux/account-discovery';
// DATA
import type { accountsTypes } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  institutionLoginsActions,
  institutionLoginsSelectors,
  institutionLoginsTypes,
  institutionLoginsUtils,
} from 'app-components/flux/institution-logins';

import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
// UTILS
import { tracker } from 'app-components/utils/core';

// LOCAL
import AccountListItem from 'components/AccountCard/AccountListItem';
import AccountManageUpdatesChip from 'components/AccountManageUpdatesChip';
import AccountRefreshChip from 'components/AccountRefreshChip';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
import { showRealEstateDialog, showVehicleDialog, VehicleDialogOrigin } from 'components/Dialogs/AssetDialog/actions';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import showManageUpdatesDialog from 'components/Dialogs/ManageUpdatesDialog/dispatcher';
import StdDialog from 'components/Dialogs/StdDialog';
import Dump from 'components/Dump';
import { AppleWalletErrorBanner, InstitutionLoginStatusBanner } from 'components/FiStatusBanner';
import InstitutionIcon from 'components/InstitutionIcon';
import InstitutionItem from 'components/InstitutionItem';
import NestedQMenu from 'components/NestedQMenu';
import QButton, { QButtonVariantEnum } from 'components/QButton';

import InstitutionErrorBanner from './InstitutionErrorBanner';
import { hasConnectedAccounts } from './selectors';
import styles from './styles';

// @ts-expect-error FIXME TS2345
const useStyles = makeStyles()(styles);

type Props = {
  institutionLogin?: institutionLoginsTypes.InstitutionLogin; // Note - undefined for manual accounts card
  id: string;
  title?: string;
  accounts: ImList<accountsTypes.Account> | null;
};

const AccountCard = (props: Props) => {
  const { institutionLogin, id, title, accounts } = props;

  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const theme = useTheme();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showExpandedMenu, setShowExpandedMenu] = useState(false);
  const [updatedInstitutionName, setUpdatedInstitutionName] = useState<string | null>(null);
  const [showEditDialog, toggleShowEditDialog] = useToggle(false);

  const aggLoading = useAppSelector((state) =>
    institutionLogin
      ? (institutionLoginsSelectors.getIdsRefreshing(state) as Set<string>)?.includes(institutionLogin.id)
      : false,
  );
  const aggAction = useAppSelector((state) =>
    aggErrorsSelectors.getAggregationAction(state, { institutionLoginId: institutionLogin?.id ?? '' }),
  );

  const a2aTransferEnabled = useAppSelector(a2aTransfersSelectors.a2aTransferEnabled);
  const institution = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionForInstitutionLogin(state, institutionLogin?.id),
  );
  const loginHasConnectedAccountsProps = useMemo(
    () => ({ institutionLoginId: institutionLogin?.id || null }),
    [institutionLogin],
  );
  const loginHasConnectedAccounts = useAppSelector((state) =>
    // @ts-expect-error FIXME TS2345
    hasConnectedAccounts(state, loginHasConnectedAccountsProps),
  );
  const fiStatusEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'fiStatus'));

  const isReportableError = aggAction && institutionLoginsUtils.isErrorType(aggAction?.status);
  const isZillow = institutionLogin?.channel === 'ZILLOW';
  const isKbb = institutionLogin?.channel === 'KELLEY_BLUE_BOOK';
  const kickoffA2AFlow = useKickoffA2AFlow();

  const isRefreshFrequencySupported = useAppSelector((state) =>
    institutionLoginsSelectors.isRefreshFrequencySupported(state, institutionLogin?.id || ''),
  );

  // @ts-expect-error FIXME TS7006
  const handleOpenMenu = useCallback((e) => {
    setMenuAnchorEl(e.currentTarget);
    if (e.shiftKey) setShowExpandedMenu(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setShowExpandedMenu(false);
  }, []);

  const handleUpdateInstitutionName = useCallback(() => {
    toggleShowEditDialog();

    if (institutionLogin) {
      dispatch(
        institutionLoginsActions.updateInstitutionLogin(
          institutionLoginsTypes.mkInstitutionLogin({
            id: institutionLogin.id,
            institutionId: institutionLogin.institutionId,
            name: updatedInstitutionName as string,
          }),
        ),
      );
      tracker.track(tracker.events.fiLoginNameSave, { institution_id: institutionLogin?.institutionId || '0' });
    }
  }, [institutionLogin, dispatch, updatedInstitutionName, toggleShowEditDialog]);

  const deleteAccount = useCallback(() => {
    actionHelpers
      .bindPromiseAction(
        dispatch,
        institutionLoginsActions.deleteInstitutionLogin,
      )(institutionLogin)
      .then(() => {
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'fi-deletion-confirmation-dialog',
              type: CONFIRMATION_DIALOG_TYPE,
              props: ImmutableMap(
                mkConfirmationDialogProps({
                  title: 'Delete Financial Institution Success',
                  content: (
                    <Typography variant='titleRegular'>
                      {' '}
                      Your financial institution has been successfully deleted.
                    </Typography>
                  ),
                }),
              ),
            }),
          ),
        );
      })
      .catch((error) => {
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'fi-deletion-failure-dialog',
              type: CONFIRMATION_DIALOG_TYPE,
              props: ImmutableMap(
                mkConfirmationDialogProps({
                  title: 'Financial Institution Deletion Failure',
                  content: (
                    // @ts-expect-error FIXME TS2339
                    <Typography variant='titleMedium' className={classes.errorWrapper}>
                      {error.response?.data?.errors[0]?.detail ||
                        error.response?.data?.errors[0]?.title ||
                        'Sorry, there seems to be a problem on our end. Please try again later.'}
                      {error?.response?.status && (
                        <Typography
                          // @ts-expect-error FIXME TS2339
                          className={classes.errorCodeText}
                        >{`Error Code: ${error?.response?.status}`}</Typography>
                      )}
                    </Typography>
                  ),
                }),
              ),
            }),
          ),
        );
      });
    // @ts-expect-error FIXME TS2339
  }, [institutionLogin, classes.errorCodeText, classes.errorWrapper, dispatch]);
  // ====================================== Dialog Kickoffs ================================================ //
  const kickoffResetFi = useCallback(() => {
    handleCloseMenu();

    // const { institutionId, channel } = institutionLogin;
    if (institutionLogin) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'reset-login-connection',
            type: CONFIRMATION_DIALOG_TYPE,
            allowNesting: true,
            props: ImmutableMap(
              mkConfirmationDialogProps({
                title: 'Reset connection',
                content: `Resetting your connection will disconnect ${institutionLogin.name} from Quicken and prompt you to reconnect.
                If restoring connectivity is not successful the institution will remain in Quicken but will be disconnected with the ability to connect it at a later point in time.`,
                confirmLabel: 'Reset',
                denyLabel: 'Cancel',
                confirmAction: () =>
                  actionHelpers
                    .bindPromiseAction(
                      dispatch,
                      institutionLoginsActions.qcsInstitutionLoginAction,
                    )({ qcsAction: 'disconnect', id: institutionLogin.id, params: { doSynchronous: true } })
                    .then(() => dispatch(accountDiscoveryActions.doUpdateFi({ institutionLogin }))),
              }),
            ),
          }),
        ),
      );
    }
  }, [handleCloseMenu, dispatch, institutionLogin]);

  const kickoffShowManageUpdates = useCallback(() => {
    if (institutionLogin) {
      handleCloseMenu();
      showManageUpdatesDialog({ defaultValue: institutionLogin.id });
    }
  }, [institutionLogin, handleCloseMenu]);

  const kickoffCloseFi = useCallback(() => {
    handleCloseMenu();
    if (institutionLogin) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'close-institution',
            type: CONFIRMATION_DIALOG_TYPE,
            allowNesting: true,
            props: ImmutableMap(
              mkConfirmationDialogProps({
                title: 'Close institution',
                content: `Closing ${institutionLogin?.name} will set the account balances to $0 and disable downloading of future transactions in Quicken.
                 You will still be able to access all of your old transactions`,
                confirmLabel: 'Confirm',
                denyLabel: 'Cancel',
                confirmAction: () =>
                  actionHelpers.bindPromiseAction(
                    dispatch,
                    institutionLoginsActions.qcsInstitutionLoginAction,
                  )({ qcsAction: 'close', id: institutionLogin?.id }),
              }),
            ),
          }),
        ),
      );
    }
  }, [handleCloseMenu, institutionLogin, dispatch]);

  const kickoffDeleteFi = useCallback(() => {
    handleCloseMenu();

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-institution',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Delete institution',
              content: `Deleting ${institutionLogin?.name} will remove all of its data and transactions from Quicken.
               This will not fix connection issues.`,
              confirmLabel: 'Delete',
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              denyLabel: 'Cancel',
              confirmAction: () => deleteAccount(),
            }),
          ),
        }),
      ),
    );
  }, [handleCloseMenu, dispatch, institutionLogin, deleteAccount]);

  const handleRequestCreate = useCallback(() => {
    handleCloseMenu();
    dispatch(accountDiscoveryActions.doAccountRediscovery(institutionLogin, { dialogTitle: 'Add account' }));
  }, [dispatch, handleCloseMenu, institutionLogin]);

  const kickoffEditFi = useCallback(() => {
    handleCloseMenu();
    toggleShowEditDialog();
  }, [handleCloseMenu, toggleShowEditDialog]);

  const kickoffDisconnectFi = useCallback(() => {
    handleCloseMenu();
    if (institutionLogin) {
      actionHelpers.bindPromiseAction(
        dispatch,
        institutionLoginsActions.qcsInstitutionLoginAction,
      )({ qcsAction: 'disconnect', id: institutionLogin?.id });
    }
  }, [handleCloseMenu, dispatch, institutionLogin]);

  const kickoffConnectFi = useCallback(() => {
    handleCloseMenu();
    dispatch(accountDiscoveryActions.doUpdateFi({ institutionLogin }));
  }, [dispatch, handleCloseMenu, institutionLogin]);

  const kickoffZillowDialog = useCallback(() => {
    handleCloseMenu();
    dispatch(showRealEstateDialog());
  }, [dispatch, handleCloseMenu]);

  const kickoffKbbDialog = useCallback(() => {
    handleCloseMenu();
    dispatch(showVehicleDialog({ dialogOrigin: VehicleDialogOrigin.AddAccount }));
  }, [dispatch, handleCloseMenu]);

  // ====================================== Memoized Values ================================================ //

  const menuOptions = () => {
    const retOptions: any = [];
    if (institutionLogin?.channel === 'APPLE_WALLET') {
      retOptions.push({
        label: 'Edit institution',
        onSelect: kickoffEditFi,
        divider: true,
        startIcon: <EditIcon className={classes.icons} />,
      });
    } else {
      if (institutionLogin?.isConnected && !isReportableError) {
        retOptions.push({
          label: 'Add a new account',
          onSelect: () => {
            if (isZillow) {
              kickoffZillowDialog();
              return;
            }
            if (isKbb) {
              kickoffKbbDialog();
              return;
            }
            handleRequestCreate();
          },
          divider: false,
          startIcon: <AddCircleIcon className={classes.icons} />,
        });
      }
      if (isReportableError) {
        retOptions.push({
          label: 'Fix error',
          onSelect: () => {
            if (!aggLoading) {
              aggErrors.kickoffAggAction(dispatch, aggAction, institutionLogin, 'account settings');
              handleCloseMenu();
            }
          },
          divider: true,
          startIcon: <ErrorRoundedIcon className={classes.icons} />,
          disabled: aggLoading,
        });
      }
      if (institutionLogin) {
        retOptions.push({
          label: 'Edit institution',
          onSelect: kickoffEditFi,
          divider: true,
          startIcon: <EditIcon className={classes.icons} />,
        });
      }
      if (!isZillow && !isKbb && institutionLogin && a2aTransferEnabled) {
        retOptions.push({
          label: 'Set up account transfers',
          onSelect: () => {
            if (institutionLogin?.institutionId) {
              handleCloseMenu();
              kickoffA2AFlow({
                initiator: 'FI account details',
                startTabIndex: onboardA2ADialogIndexEnums.SELECT_ACCT,
                initialInstitutionId: institutionLogin.institutionId,
              });
            }
          },
          divider: true,
          startIcon: <SwapHorizRoundedIcon className={classes.icons} />,
        });
      }
      if (!loginHasConnectedAccounts && !isZillow && !isKbb && !accounts?.every((acc) => acc.isClosed ?? false)) {
        retOptions.push({
          label: 'Connect',
          onSelect: kickoffConnectFi,
          id: 'login-connect',
          startIcon: <ExploreIcon className={classes.icons} />,
        });
      }
      if (loginHasConnectedAccounts) {
        retOptions.push({
          label: 'Reset connection',
          onSelect: kickoffResetFi,
          id: 'login-reset',
          startIcon: <RefreshIcon className={classes.icons} />,
        });
        if (isRefreshFrequencySupported) {
          retOptions.push({
            label: 'Manage updates',
            onSelect: kickoffShowManageUpdates,
            id: 'manage-updates',
            startIcon: <ManageHistoryIcon className={classes.icons} />,
          });
        }
        retOptions.push({
          label: 'Mark as closed',
          onSelect: kickoffCloseFi,
          id: 'login-close',
          divider: true,
          startIcon: <NotInterestedRoundedIcon className={classes.icons} />,
        });
      }
      if (showExpandedMenu && loginHasConnectedAccounts) {
        retOptions.push({
          label: 'Disconnect institution',
          onSelect: kickoffDisconnectFi,
          id: 'login-disconnect',
          startIcon: <ExploreOffIcon className={classes.icons} />,
        });
      }
    }
    retOptions.push({
      customRender: (
        <Typography
          variant='bodyRegular'
          style={{ color: theme.palette.amount.negative, display: 'flex', alignItems: 'center' }}
        >
          <DeleteIcon style={{ marginRight: theme.spacing(1) }} />
          Delete institution
        </Typography>
      ),
      onSelect: kickoffDeleteFi,
      id: 'login-delete',
    });

    return retOptions;
  };

  const updatedInstitutionNameErrorMessage = useMemo(() => {
    if (updatedInstitutionName === null || !institutionLogin) return null;
    if (updatedInstitutionName.length === 0) return 'New name must be more than 0 characters';
    if (updatedInstitutionName === institutionLogin?.name || updatedInstitutionName === title)
      return 'New name must be different from current name';
    return null;
  }, [updatedInstitutionName, institutionLogin, title]);

  return (
    <Paper id={id}>
      <Stack gap={2} padding={2}>
        {/* ============================================ Header ============================================ */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={2}
        >
          <Dump obj={institutionLogin} />
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2} width='100%'>
            <InstitutionItem value={institutionLogin?.id} title={title} id={`${id}-title`} />
            {title === 'Manual Accounts' && (
              <div className={classes.buttonContainer}>
                <QButton
                  size='small'
                  variant='contained'
                  onClick={() => {
                    // onHandleCloseOverride();
                    dispatch(
                      accountDiscoveryActions.doAccountDiscovery({
                        initiator: 'manual accounts',
                        mode: 'settings accounts link',
                      }),
                    );
                  }}
                  startIcon={<LinkIcon />}
                >
                  Link Financial Institutions
                </QButton>
              </div>
            )}
          </Stack>
          {institutionLogin && (
            <Stack
              direction='row'
              gap={1}
              alignItems='center'
              justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
              width='100%'
            >
              <AccountRefreshChip institutionLoginId={institutionLogin.id} />
              <AccountManageUpdatesChip institutionLoginId={institutionLogin.id} />
              <IconButton
                size='medium'
                variant='tonal'
                shape='rounded'
                id='login-menu-button'
                aria-label='Login Menu'
                onClick={handleOpenMenu}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
        <NestedQMenu
          id='login-menu'
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          options={menuOptions()}
        />

        {/* ============================================ Content ============================================ */}
        {accounts && (
          <Stack gap={1}>
            {institutionLogin?.channel === 'APPLE_WALLET' ? (
              <AppleWalletErrorBanner institutionLoginId={institutionLogin?.id} />
            ) : fiStatusEnabled && institutionLogin ? (
              <InstitutionLoginStatusBanner
                institutionLoginId={institutionLogin.id}
                className={classes.statusBanner}
                location='account settings'
                showBoth={true}
              />
            ) : (
              <InstitutionErrorBanner institutionLogin={institutionLogin} location='account settings' />
            )}
            <List sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.25 }}>
              {accounts.map((account, index) => (
                <React.Fragment key={account.id || account.clientId}>
                  <AccountListItem
                    account={account}
                    institutionLogin={institutionLogin}
                    index={index}
                    errorBannerShowing={isReportableError}
                  />
                  {index < accounts.size - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Stack>
        )}
      </Stack>
      {/* ========================================== Edit Dialog ========================================== */}
      <StdDialog
        onClose={toggleShowEditDialog}
        open={showEditDialog}
        showCloseButton
        size='sm'
        title='Edit institution'
      >
        <DialogContent className={classes.dialogContent}>
          <div className={classes.dialogContentInner}>
            <InstitutionIcon institution={institution} />
            <div style={{ marginLeft: 10, flexGrow: 1 }}>
              <TextField
                id='institution-name'
                label='Name'
                style={{ width: '100%' }}
                variant='filled'
                error={Boolean(updatedInstitutionNameErrorMessage)}
                defaultValue={institutionLogin?.name || title}
                onChange={(e) => setUpdatedInstitutionName(e.target.value)}
                helperText={updatedInstitutionNameErrorMessage}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <QButton id='cancel-institution' variant='outlined' onClick={toggleShowEditDialog}>
            Cancel
          </QButton>
          <QButton
            id='update-institution'
            variant='contained'
            disabled={Boolean(!updatedInstitutionName || updatedInstitutionNameErrorMessage)}
            onClick={handleUpdateInstitutionName}
          >
            Update
          </QButton>
        </DialogActions>
      </StdDialog>
    </Paper>
  );
};

export default AccountCard;
